import React from 'react';
import { parseExchangeTime } from './marketdataTime';
import { CHART_RANGES } from './MarketdataRangeSelector';

// Kerzen-Datum in ein lesbares Label umwandeln - 1T/1W liefern Datum+Uhrzeit
// ("YYYY-MM-DD HH:mm:ss", Boersenzeitzone, siehe parseExchangeTime()), 1M/6M/
// 1J/5J liefern reines Datum ("YYYY-MM-DD") ohne Uhrzeit (siehe
// MarketdataChart.inc/MarketdataCharts.inc - analog MarketdataDetailPanel.js).
function formatLabel(dateString, range) {
	if (!dateString) {
		return '';
	}
	const isIntraday = CHART_RANGES.find((r) => r.code === range)?.intraday ?? true;
	if (isIntraday) {
		const localDate = parseExchangeTime(dateString);
		let label = localDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		if (range === '1W') {
			label = localDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' + label;
		}
		return label;
	}
	const [year, month, day] = dateString.split('-');
	return `${day}.${month}.${(range === '5J') ? year : year.slice(2)}`;
}

// Einzelne Mini-Chart-Kachel im Charts-Modul (siehe MarketdataCharts.js).
// Bewusst reines SVG statt recharts: bei einer ganzen Watchlist gleichzeitig
// (10+ Kacheln) hat je eine eigene recharts-<ResponsiveContainer>-Instanz
// (ResizeObserver + Re-Layout pro Kachel) spuerbare Ladezeit verursacht - und
// zwar JEDES Mal, wenn das Modul sichtbar wird (Module.js versteckt nur per
// CSS display:none, entlaedt nicht - der Resize-Sprung 0px -> echte Breite
// loeste bei jedem Oeffnen erneut das Re-Layout aller Kacheln aus). Ein SVG
// mit viewBox skaliert dagegen rein per CSS responsiv, ganz ohne
// JS-Resize-Messung - fuer eine einfache Linie ohne Achsen/Tooltip (wie hier
// gebraucht) ausreichend und um ein Vielfaches billiger.
export default function MarketdataChartTile({ symbol, shortlabel, candles, range }) {
	const rows = (candles || []).filter((row) => row.close != null);

	const first = rows[0]?.close;
	const last = rows[rows.length - 1]?.close;
	const lineColor = (last == null || first == null || last === first) ? '#5f8c6c' : (last > first ? '#1a7f37' : '#cf222e');

	const VIEW_WIDTH = 300;
	const VIEW_HEIGHT = 100;
	const PADDING = 6;

	let points = '';
	if (rows.length > 1) {
		const closes = rows.map((row) => row.close);
		const min = Math.min(...closes);
		const max = Math.max(...closes);
		const range = (max - min) || 1;
		points = rows.map((row, index) => {
			const x = (index / (rows.length - 1)) * (VIEW_WIDTH - 2 * PADDING) + PADDING;
			const y = VIEW_HEIGHT - PADDING - ((row.close - min) / range) * (VIEW_HEIGHT - 2 * PADDING);
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		}).join(' ');
	}

	const firstTime = formatLabel(rows[0]?.date, range);
	const lastTime = formatLabel(rows[rows.length - 1]?.date, range);

	function formatPrice(value) {
		return value == null ? '–' : value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	return (
		<div className="marketdata-chart-tile">
			<div className="marketdata-chart-tile-header">
				<span className="marketdata-chart-tile-title">{shortlabel || symbol}</span>
				<span className="marketdata-chart-tile-price" style={{ color: lineColor }}>{formatPrice(last)}</span>
			</div>
			{rows.length < 2
				?
				<div className="marketdata-detail-hint">Keine Chart-Daten verfügbar.</div>
				:
				<React.Fragment>
					<svg className="marketdata-chart-tile-svg" viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`} preserveAspectRatio="none">
						<polyline points={points} fill="none" stroke={lineColor} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
					</svg>
					<div className="marketdata-chart-tile-times">
						<span>{firstTime}</span>
						<span>{lastTime}</span>
					</div>
				</React.Fragment>
			}
		</div>
	);
}
